import { Decimal, ZERO } from '@insio/decimal';
import type { InvoiceLine } from '../../../../graphql/generated/graphql';

export type InvoiceLineItem = Partial<InvoiceLine>;

/** Tolerant Decimal coercion — runtime values may be Decimal, number or string. */
const toDecimal = (value: unknown): Decimal | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Decimal) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    try {
      const d = new Decimal(value);
      return d.isNaN() ? null : d;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Grand total shown next to the line-items header. VAT documents sum the
 * gross per-line total, non-VAT documents the net one — mirroring the per-line
 * display. Returns `null` (→ render nothing) when there are no lines or any
 * line is missing its total, so an incomplete sum never shows as `0`.
 */
export const computeLineItemsGrandTotal = (
  lines: readonly InvoiceLineItem[],
  isVatDocument: boolean
): Decimal | null => {
  if (lines.length === 0) return null;
  let sum = ZERO;
  for (const line of lines) {
    const val = toDecimal(isVatDocument ? line.totalAmountInclVat : line.totalAmountNoVat);
    if (val === null) return null;
    sum = sum.plus(val);
  }
  return sum;
};

export const isInvoiceLine = (value: unknown): value is InvoiceLineItem =>
  typeof value === 'object' &&
  value !== null &&
  ('description' in value ||
    'quantity' in value ||
    'unitPrice' in value ||
    'totalAmountInclVat' in value ||
    'totalAmountNoVat' in value);

const hasInvoiceLinesArray = (value: unknown): value is { invoiceLines: unknown[] } =>
  typeof value === 'object' &&
  value !== null &&
  Array.isArray((value as { invoiceLines?: unknown }).invoiceLines);

/**
 * Normalize either shape the FieldEditor pipeline may hand to the
 * invoice-lines editor/formatter into a plain line array. The host typically
 * passes `InvoiceLine[]`, but after a save EditableField holds the committed
 * `{ invoiceLines, isUnitPriceInclVat }` payload (via `handleCommit`'s
 * `setValue`). Returning `[]` for unrecognized shapes avoids crashes.
 */
export const toInvoiceLineItems = (value: unknown): InvoiceLineItem[] => {
  if (Array.isArray(value)) return value.filter(isInvoiceLine);
  if (hasInvoiceLinesArray(value)) return value.invoiceLines.filter(isInvoiceLine);
  return [];
};
